const BODY_PARTS = {
  TORSO: [12, 13],
  UPPER_LEG: [14, 15, 16, 17],
  LOWER_LEG: [18, 19, 20, 21],
  ARMS: [2, 3, 4, 5, 6, 7, 8, 9]
};

function getTargetParts(productTitle = '') {
  if (/saree|sari|lehenga|skirt|gharara|sharara|anarkali|gown|dress|frock|maxi/i.test(productTitle)) {
    return [...BODY_PARTS.UPPER_LEG, ...BODY_PARTS.LOWER_LEG];
  }
  if (/top|blouse|crop|shirt/i.test(productTitle)) {
    return BODY_PARTS.TORSO;
  }
  return [...BODY_PARTS.UPPER_LEG, ...BODY_PARTS.LOWER_LEG];
}

let bodyPixNetPromise = null;
function getBodyPixNet() {
  if (!bodyPixNetPromise) {
    bodyPixNetPromise = bodyPix.load({
      architecture: 'MobileNetV1',
      outputStride: 16,
      multiplier: 0.75,
      quantBytes: 2
    });
  }
  return bodyPixNetPromise;
}

class SwatchColorExtractor {
  static async extract(imageUrl, opts = {}) {
    const targetPartIds = getTargetParts(opts.productTitle);

    const img = await this._loadImage(imageUrl);
    const net = await getBodyPixNet();

    const segmentation = await net.segmentPersonParts(img, {
      internalResolution: 'medium',
      segmentationThreshold: 0.7
    });

    const canvas = document.createElement('canvas');
    canvas.width = segmentation.width;
    canvas.height = segmentation.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, segmentation.width, segmentation.height);

    const imageData = ctx.getImageData(0, 0, segmentation.width, segmentation.height).data;
    const buckets = new Map();

    for (let i = 0; i < segmentation.data.length; i++) {
      const partId = segmentation.data[i];
      if (!targetPartIds.includes(partId)) continue;

      const pixelIndex = i * 4;
      const r = imageData[pixelIndex];
      const g = imageData[pixelIndex + 1];
      const b = imageData[pixelIndex + 2];

      if (this._isNearWhiteOrBlack(r, g, b)) continue;

      const key = `${r >> 5}-${g >> 5}-${b >> 5}`;
      const bucket = buckets.get(key) || { r: 0, g: 0, b: 0, count: 0 };
      bucket.r += r;
      bucket.g += g;
      bucket.b += b;
      bucket.count += 1;
      buckets.set(key, bucket);
    }

    if (buckets.size === 0) {
      throw new Error('No solid color pixels found in the target body part');
    }

    const dominant = [...buckets.values()].sort((a, b) => b.count - a.count)[0];
    const r = Math.round(dominant.r / dominant.count);
    const g = Math.round(dominant.g / dominant.count);
    const b = Math.round(dominant.b / dominant.count);

    return { hex: this._rgbToHex(r, g, b), rgb: [r, g, b] };
  }

  static _isNearWhiteOrBlack(r, g, b) {
    const brightness = (r + g + b) / 3;
    return brightness > 235 || brightness < 20;
  }

  static _rgbToHex(r, g, b) {
    return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
  }

  static _loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Image load failed: ' + src));
      img.src = src;
    });
  }
}

window.SwatchColorExtractor = SwatchColorExtractor;

function applyColorToSwatch(swatch, hex) {
  swatch.style.setProperty('--swatch-background', `linear-gradient(to right, ${hex}, ${hex})`);
  swatch.dataset.colorLoading = 'false';
}

document.addEventListener('DOMContentLoaded', async () => {
  const swatches = Array.from(document.querySelectorAll('.color-swatch[data-feature-image]'));
  const pending = [];

  for (const swatch of swatches) {
    const imageUrl = swatch.dataset.featureImage;

    if (!imageUrl || imageUrl.startsWith('Liquid error')) {
      swatch.dataset.colorLoading = 'false';
      continue;
    }

    const cacheKey = `swatch-color-${imageUrl}`;
    const cachedHex = localStorage.getItem(cacheKey);

    if (cachedHex) {
      applyColorToSwatch(swatch, cachedHex);
    } else {
      pending.push({ swatch, imageUrl, cacheKey });
    }
  }

  if (pending.length === 0) return;

  for (const { swatch, imageUrl, cacheKey } of pending) {
    try {
      const result = await SwatchColorExtractor.extract(imageUrl, {
        productTitle: swatch.dataset.productTitle
      });
      localStorage.setItem(cacheKey, result.hex);
      applyColorToSwatch(swatch, result.hex);
    } catch (err) {
      console.warn('Swatch color extraction failed:', err);
      swatch.dataset.colorLoading = 'false';
    }
  }
});